import { useAppSelector } from 'app/config/storeConfig/hooks';
import { LangSwitcher } from 'features/LangSwitcher';
import React, { FC, memo, ReactNode, useCallback, useMemo } from 'react';
import { HeaderAuthAction } from 'features/HeaderAuthAction';
import { AppLogo } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { getLoggedUserData } from 'entities/User';
import { AppRoutes } from 'app/config/routerConfig/types';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as BookingIcon } from 'shared/assets/icons/newIcons/BookingIcon.svg';
import { Button } from 'shared/ui/Button';
import { ReactComponent as LeftIcon } from 'shared/assets/icons/newIcons/LeftArrow.svg';
import { getMobileHeaderByRoute } from 'shared/utils/helpers/getMobileHeaderByRoute';

export const CustomerHeader: FC = memo(() => {
  const { t } = useAppTranslation(['header', 'common']);
  const navigate = useNavigate();
  const location = useLocation();

  const loggedUserData = useAppSelector(getLoggedUserData);

  const actions = useMemo<ReactNode[]>(() => {
    return [
      <LangSwitcher key="1" mobileBackgroundClassName="bg-backgroundColor" />,
      <HeaderAuthAction key="2" userEmail={loggedUserData?.email} />,
    ];
  }, [loggedUserData?.email]);

  const hasRouteHistory = window.history.length > 1;

  const goBack = useCallback(() => {
    if (hasRouteHistory) {
      navigate(-1);
    }
  }, [hasRouteHistory, navigate]);

  return (
    <div className="sticky top-0 z-20 modern-booking-header">
      {/* Container: white on desktop, backgroundColor on mobile */}
      <div className="pt-[20px] pb-2 bg-backgroundColor desktop:bg-white desktop:rounded-lg desktop:shadow-sm desktop:p-4 desktop:px-[50px] desktop:h-[70px]">
        {/* Desktop: single row; Mobile: two rows */}
        <div className="hidden desktop:flex items-center justify-between">
          <div className="flex items-center shrink-0">
            <AppLogo />
          </div>
          <div className="flex items-center mx-[50px] flex-1 min-w-0">
            <Link to={AppRoutes.BOOKING} className="text-xl font-tektur">
              {t('Booking')}
            </Link>
          </div>

          <div className="flex items-center space-x-3 shrink-0">{actions}</div>
        </div>

        {/* Mobile two-rows layout */}
        <div className="desktop:hidden">
          {/* Row 1: centered logo with actions on the right */}
          <div className="relative flex items-center justify-center px-4 py-3">
            <Link to={AppRoutes.BOOKING} className="bg-trasparent p-2 rounded-md absolute left-4 top-2 border-1.5 border-primary">
              <BookingIcon className="w-[24px] h-[24px]" />
            </Link>
            <div className="flex items-center">
              <AppLogo />
            </div>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center space-x-1">{actions}</div>
          </div>
          <div className="pt-5 pb-4 flex items-center justify-center">
            <div className="absolute left-4">
              <Button
                theme="outlined"
                size="small"
                containerClassName="flex justify-start"
                className="desktop:hidden w-[40px] h-[40px] p-0 border-black shrink-0 mr-2 rounded-lg flex items-center justify-center"
                icon={<LeftIcon className="text-grey w-[16px] h-[16px] text-primary" />}
                onClick={goBack}
              />
            </div>
            <div className="text-xl font-semibold desktop:hidden font-tektur text-center">
              {t(getMobileHeaderByRoute(location.pathname), { ns: 'common' })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
